<template>
  <BaseLayout :title="`Evaluation Detail`">
    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border" role="status">
        <span class="sr-only">&nbsp;&nbsp;Loading...</span>
      </div>
    </div>
    <div v-else>
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ evaluation.deadline.homework.title }}</h5>
          <p v-if="evaluation.student" class="mb-0">Student UCO: {{ evaluation.student.uco }}</p>
        </div>
        <div class="card-header">
            <p class="card-text">
            <i class="bi bi-calendar-event text-primary me-2"></i>
            <strong>Deadline:</strong> 
            {{ formatDate(evaluation.deadline.date) }}
            </p>
            <p class="card-text">
            <i class="bi bi-flag text-primary me-2"></i>
            <strong>Deadline type:</strong> 
            {{ getEvaluationTypeLabel(evaluation.deadline.evaluation_type) }}
            </p>
            <p class="card-text">
            <i class="bi bi-clock text-primary me-2"></i>
            <strong>Evaluation started:</strong> 
            {{ formattedEvaluationStarted }}
            </p>
            <p class="card-text">
            <i class="bi bi-clock-fill text-primary me-2"></i>
            <strong>Evaluation finished:</strong> 
            {{ formattedEvaluationEnded }}
            </p>
          <p v-if="evaluation.subevaluations_passed === true" class="card-text">
            <i class="bi bi-clipboard-check text-primary me-2"></i>
            <strong>Homework passed:  </strong>  
            <i :class="'bi bi-check-circle-fill text-success'"></i>
          </p>
          <p v-else-if="evaluation.subevaluations_passed === false" class="card-text">
            <i class="bi bi-clipboard-x text-primary me-2"></i>
            <strong>Homework failed:  </strong>  
            <i :class="'bi bi-x-circle-fill text-danger'"></i>
          </p>
          <p v-else class="card-text">
            <i class="bi bi-clipboard text-primary me-2"></i>
            <strong>Evaluation is running:  </strong>  
            <i :class="'bi bi-hourglass-split text-warning'"></i>
          </p>
        </div>
      </div>
      <!-- Subevaluation Section -->
      <div class="alert alert-danger mt-3" role="alert" v-if="sortedSubtasks && sortedSubtasks.length &&
                              evaluation.evaluation_started && evaluation.evaluation_ended &&
                              evaluation.in_queue != true && evaluation.container_error == true">
        <p class="card-text">Internal error while running evaluation.</p>
      </div>
      <!-- Display when re-evaluation was canceled -->
      <div class="alert alert-info mt-3" role="alert" v-if="sortedSubtasks && sortedSubtasks.length &&
                              evaluation.evaluation_started && evaluation.evaluation_ended &&
                              evaluation.in_queue != true && evaluation.canceled == true">
        <p class="card-text">Re-evaluation was cancelled. Last complete evaluation is displayed.</p>
      </div>
      
      <!-- Display task to student: only only if evaluation has started and ended and and not in queue and it is not internal error-->
      <!-- Display task to teacher: only only if evaluation has started and ended and is not in queue -->
      <div class="card mb-3" v-if="sortedSubtasks && sortedSubtasks.length &&
                              evaluation.evaluation_started && evaluation.evaluation_ended &&
                              evaluation.in_queue != true && (evaluation.container_error != true || isTeacher)">
        <h5 class="card-header">Tasks</h5>
        <div v-for="subtask in sortedSubtasks" :key="subtask.id" class="card-body">
            <DropDownSubEvaluationView
            :subtask="subtask"
            :deadline="evaluation.deadline"
            :subevaluation="evaluation.subevaluations.find(subeval => subeval.submission.subtask_id === subtask.id)"
            :submission="evaluation.subevaluations.find(subeval => subeval.submission.subtask_id === subtask.id)?.submission"
          />
        </div>
      </div>
      <!-- Display when First evaluation (without times) was canceled -->
      <div v-else-if="evaluation.canceled == true" class="alert alert-info mt-3" role="alert">
        <p class="card-text">Evaluation was cancelled.</p>
      </div>
      <div v-else-if="evaluation.container_error != true" class="alert alert-info mt-3" role="alert">
        <p class="card-text">Evaluation is not finished.</p>
      </div>
      <!-- Accordion Container Log for Teachers -->
      <div class="accordion mb-3" id="containerLogAccordion" v-if="isTeacher">
        <div class="accordion-item">
          <h2 class="accordion-header" id="headingContainerLog">
            <button
              class="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseContainerLog"
              aria-expanded="false"
              aria-controls="collapseContainerLog"
              @click="handleAccordionShown"
            >
              Container Log:
            </button>
          </h2>
          <div
            id="collapseContainerLog"
            class="accordion-collapse collapse"
            aria-labelledby="headingContainerLog"
            data-bs-parent="#containerLogAccordion"
            ref="collapseElement"
          >
            <div class="accordion-body">
              <div v-if="containerContent">
                <p class="card-text p-2 border rounded">{{ containerContent }}</p>
              </div>
              <div v-if="containerLoading" class="text-center my-4">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="!loading" class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link :to="`/homework/${evaluation.deadline.homework.id}`" class="btn btn-primary">
          <i class="bi bi-arrow-right-circle me-2"></i> Go to Homework
        </router-link>
        <button 
          v-if="isTeacher" 
          class="btn btn-warning mt-2" 
          @click="downloadFilesForPlagiateDetection"
          :disabled="plagiateGenerating"
        >
          <span v-if="plagiateGenerating" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-download me-2"></i>
          {{ plagiateGenerating ? 'Generating...' : 'Download files for Plagiate verification' }}
        </button>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import DropDownSubEvaluationView from '@/components/DropDownSubEvaluationView.vue';
import EvaluationController from '@/controllers/EvaluationController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import Evaluation from '@/models/evaluation';
import Deadline from '@/models/deadline';
import { mapGetters } from 'vuex';

export default {
  name: 'EvaluationDetailView',
  components: {
    BaseLayout,
    DropDownSubEvaluationView,
    StemErrorModal
  },
  computed: {
    ...mapGetters(['isTeacher']),
    formattedEvaluationStarted() {
      return this.evaluation && this.evaluation.evaluation_started
        ? Evaluation.formatDate(this.evaluation.evaluation_started)
        : '';
    },
    formattedEvaluationEnded() {
      return this.evaluation && this.evaluation.evaluation_ended
        ? Evaluation.formatDate(this.evaluation.evaluation_ended)
        : '';
    },
    sortedSubtasks() {
      return (this.evaluation.deadline?.homework?.subtasks || []).slice().sort((a, b) => a.number - b.number);
    },
  },
  data() {
    return {
      evaluation: {},
      loading: true,
      containerLoading: false,
      containerContent: null,
      plagiateGenerating: false,
    };
  },
  async created() {
    try {
      const evaluationId = this.$route.params.id;
      this.evaluation = await EvaluationController.fetchEvaluationByIdWithRelatedData(evaluationId);
    } catch (error) {
      this.$store.dispatch('triggerAlert', {
          title: 'Error',
          message: error.message,
          type: 'error',
        });
      this.$router.push('/evaluations');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    handleAccordionShown() {
      if (this.isTeacher && this.evaluation.container_log_path && !this.containerContent) {
        this.containerLoading = true;
        this.fetchFileContent(this.evaluation.container_log_path);
      }
    },
    async fetchFileContent(filePath) {
      try {
        const response = await EvaluationController.downloadFile(filePath);
        const content = await response.data.text();
        this.containerContent = content;
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
        this.containerLoading = false;
      }
    },
    async downloadFilesForPlagiateDetection() {
      try {
        this.plagiateGenerating = true;
        const response = await EvaluationController.downloadFilesForPlagiateDetection(this.evaluation.student.id, this.evaluation.deadline.id);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `plagiate_detection_${this.evaluation.student.uco}_${this.evaluation.deadline.homework.title}.zip`);        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
      finally {
        this.plagiateGenerating = false;
      }
    },
    formatDate(date) {
      return Deadline.formatDateTime(date);
    },
    getEvaluationTypeLabel(type) {
      return Deadline.getEvaluationTypeLabel(type);
    },
  }
};

</script>